// ============================================================
// CalendarTimeGrid.tsx
// Day + Week time-grid for the Diary ("Make the calendar a real diary").
// ONE component drives both: `days` carries a single date (Day view) or
// seven (Week view, Mon-first). Built entirely from the app's existing
// "B in Casevo" Casevo Tailwind tokens (no raw hex), matching the month
// grid's chip language (deadline = soft red, task = mint green).
//
// LAYOUT
//   - A header row of day columns (name + date number; today highlighted
//     with the mint ring, exactly like the month grid's today cell).
//   - An ALL-DAY STRIP under the header: date-only items (case deadlines,
//     and tasks with no time-of-day) sit here as chips, plus a 🗒 / 🔔
//     indicator when the day has a note / reminder.
//   - A scrollable hourly body (START_HOUR..END_HOUR). TIMED items (tasks
//     with a real, non-midnight time) are absolutely positioned at their
//     local hour. A mint "now" line is drawn in today's column.
//
// TIMED vs ALL-DAY — mirrors the month grid + CalendarView.formatEntryTime:
// a date-only deadline arrives as midnight UTC, so we test the item's UTC
// time. An item is TIMED only when it's a task whose UTC time is non-
// midnight; everything else is all-day. A timed task is then POSITIONED by
// its LOCAL hour (what the user sees), clamped into the visible band.
//
// DATE KEYING is civil-day, same as the month grid: cells are built with
// the local Date constructor (local parts), entries/notes/reminders are
// keyed by their UTC parts. The civil integers match, so an item lands on
// the intended day regardless of timezone.
//
// Clicking a day header opens that day's panel (onDayClick). Clicking an
// item opens it: case_deadline → onOpenCase, task → onOpenTask — the same
// routing the day panel uses.
// ============================================================
import { CalMarker, IcBell, IcCalendar, IcNote } from './calendarIcons'
import React, { useMemo, useRef, useEffect, useState } from 'react'
import { SkeletonBar, fadeClassFor, type CalendarFade } from './CalendarSkeleton'
import { CalendarEntry, DayNote, Reminder } from '../../types/calendar.types'
import CalendarContextMenu, { CalendarMenuItem } from './CalendarContextMenu'
import { MenuIcon } from '../shared/menuUi'
import { ymdLocal, dueYmd } from '../tasks/myWorkLogic'
import { useLiveUserName } from '../../hooks/useLiveUserName'

// Visible band + row height. 07:00–20:00 covers a normal working day and
// keeps the grid scannable; the body scrolls if the viewport is short.
const START_HOUR = 7
const END_HOUR = 20
const HOUR_PX = 48
// Top padding inside the scroll body so the first hour's line + its
// centre-aligned "07:00" label aren't clipped where the all-day strip
// meets the grid. All absolutely-positioned rows (labels, gridlines,
// now-line, items) are offset by this, and the body height includes it.
const PAD_TOP = 10
const BODY_HEIGHT = (END_HOUR - START_HOUR + 1) * HOUR_PX + PAD_TOP

const WEEKDAY_FMT: Intl.DateTimeFormatOptions = { weekday: 'short' }

// Civil keys — local parts for cells, UTC parts for stored rows.
const localKey = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
const utcKey = (iso: string): string => {
  const d = new Date(iso)
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`
}

// Open an Outlook event in a new tab (the only action for read-only Outlook
// items). noopener/noreferrer so the opened Outlook tab can't reach back.
export const openOutlookLink = (webLink: string | null | undefined): void => {
  if (webLink) window.open(webLink, '_blank', 'noopener,noreferrer')
}

// Appointments are ALWAYS timed (they carry a real startAt). A task or a
// reminder is timed when its UTC time is non-midnight (a reminder's date is
// its remindAt when timed, else whole-day midnight-UTC); case deadlines are
// whole-day.
export const isTimedEntry = (e: CalendarEntry): boolean => {
  if (e.type === 'appointment') return true
  // Outlook events carry an explicit all-day flag from Graph.
  if (e.type === 'outlook') return !e.isAllDay
  if (e.type !== 'task' && e.type !== 'reminder') return false
  const d = new Date(e.date)
  return d.getUTCHours() !== 0 || d.getUTCMinutes() !== 0
}

// ★ The civil DAY an entry belongs on — the SAME rule as My Work
// (myWorkLogic dueYmd): a TIMED item (appointment, timed task/reminder, timed
// Outlook event) lands on its LOCAL day; a whole-day item (stored at midnight
// UTC) keeps its stored day. Keying timed items by the UTC day put e.g. a
// 00:30 BST appointment on the previous day. Shared by both grids + the day panel.
export const entryDayKey = (e: CalendarEntry): string =>
  (isTimedEntry(e) ? ymdLocal(new Date(e.date)) : utcKey(e.date))
// Reminders: a TIMED one (remindAt set) belongs on the LOCAL day of its instant;
// a whole-day one on its stored day. Uses remindAt rather than `date` because a
// rescheduled reminder's `date` is synced to the UTC day server-side.
export const reminderDayKey = (r: Pick<Reminder, 'date' | 'remindAt'>): string =>
  (r.remindAt ? ymdLocal(new Date(r.remindAt)) : dueYmd(r.date))

interface PlacedEntry {
  entry: CalendarEntry
  topPx: number
  // Appointment blocks span start→end; heightPx > 0 draws a sized block.
  // Tasks (0) render as a single-line chip at their hour.
  heightPx: number
  // Side-by-side layout (like Google/Outlook): overlapping items split the
  // column into lanes. `lane` = this item's 0-based lane; `lanes` = the total
  // lanes in its overlap cluster (1 when it overlaps nothing → full width).
  lane: number
  lanes: number
  // A LATE Outlook event (arrived after the rest of the range was revealed)
  // that overlaps a Casevo item: drawn as an inset overlay on the right of the
  // column instead of re-laning — so the Casevo items it overlaps don't move.
  overlay?: boolean
}

// A chip (task/reminder, heightPx 0) has no real duration; give it a small
// nominal height so a point-in-time item still lanes against overlapping
// blocks rather than sitting on top of them.
const CHIP_PX = 18

// Assign lanes to a day's placed items so overlapping ones sit side-by-side.
// Standard sweep: sort by start; group items into overlap CLUSTERS (any time
// intersection, transitively); within a cluster greedily pack each item into
// the first free lane; every item in a cluster gets that cluster's lane count
// (so widths are equal across the cluster). Mutates each PlacedEntry.lane/lanes.
const assignLanes = (items: PlacedEntry[]): void => {
  const iv = items.map((p) => ({ p, top: p.topPx, bottom: p.topPx + (p.heightPx > 0 ? p.heightPx : CHIP_PX) }))
  iv.sort((a, b) => a.top - b.top || a.bottom - b.bottom)
  const flush = (from: number, to: number): void => {
    const laneBottoms: number[] = [] // last bottom per lane
    for (let i = from; i < to; i++) {
      const it = iv[i]
      let lane = laneBottoms.findIndex((b) => b <= it.top)
      if (lane === -1) {
        lane = laneBottoms.length
        laneBottoms.push(it.bottom)
      } else {
        laneBottoms[lane] = it.bottom
      }
      it.p.lane = lane
    }
    for (let i = from; i < to; i++) iv[i].p.lanes = laneBottoms.length
  }
  let clusterStart = 0
  let clusterMaxBottom = -Infinity
  for (let i = 0; i < iv.length; i++) {
    if (iv[i].top >= clusterMaxBottom) {
      if (i > clusterStart) flush(clusterStart, i)
      clusterStart = i
      clusterMaxBottom = iv[i].bottom
    } else {
      clusterMaxBottom = Math.max(clusterMaxBottom, iv[i].bottom)
    }
  }
  if (clusterStart < iv.length) flush(clusterStart, iv.length)
}
interface ColumnData {
  date: Date
  key: string
  isToday: boolean
  allDay: CalendarEntry[]
  timed: PlacedEntry[]
  hasNote: boolean
  hasReminder: boolean
}

interface CalendarTimeGridProps {
  days: Date[] // 1 (day view) or 7 (week view), each local-midnight
  // The range's data isn't ready yet → every column shows a skeleton (the
  // grid itself, dates and navigation are live straight away).
  loading?: boolean
  // Which items fade in when they mount (see CalendarSkeleton.fadeClassFor).
  fade?: CalendarFade
  // Outlook arrived AFTER the rest of the range was revealed: keep the Casevo
  // items exactly where they were (no re-laning) — see PlacedEntry.overlay.
  outlookLate?: boolean
  // Small status shown in the header row (e.g. "Loading Outlook…").
  statusSlot?: React.ReactNode
  entries: CalendarEntry[]
  dayNotes: DayNote[]
  reminders: Reminder[]
  onPrev: () => void
  onNext: () => void
  onToday: () => void
  // Stage 3: a day HEADER click switches to Day view for that date.
  onViewDay: (date: Date) => void
  onOpenTask: (taskId: string) => void
  onOpenCase: (caseId: string) => void
  // Open an appointment for edit (the appointment form).
  onOpenAppointment: (appointmentId: string) => void
  // Clicking a reminder chip opens that day's panel (its "Remind me"
  // section, where the reminder is edited). Passed the column's date.
  onOpenReminder: (date: Date) => void
  // Click-to-add: create at a specific date + hour (hour null = all-day,
  // from the all-day strip). The parent seeds the create flow (task carries
  // the time; deadline is whole-day).
  onNewTaskAt: (date: Date, hour: number | null) => void
  onNewDeadlineAt: (date: Date, hour: number | null) => void
  onNewAppointmentAt: (date: Date, hour: number | null) => void
  // Stage 4: right-click a slot/all-day cell/header → the add-menu. hour is
  // the clicked hour (null for the all-day strip / header).
  onContextMenu: (e: React.MouseEvent, date: Date, hour: number | null) => void
  // Heading label for the current range (e.g. "Mon 21 Sep" or
  // "22–28 Sep 2026"), built by the parent so Day/Week read consistently.
  rangeLabel: string
  // Drag-to-move: persist a timed appointment/reminder/task dropped at a new
  // instant (ISO, local wall-clock). The parent shifts startAt/endAt (appt,
  // keeping duration), remindAt (reminder) or dueDate (task), incl. the
  // recurring-occurrence (Stage-2 override) path.
  onMoveEntry: (entry: CalendarEntry, newStartISO: string) => void
  // Right-click menu actions (per type). Complete/delete for deadlines are
  // omitted here: a case_deadline calendar entry carries no deadline-row id
  // (only caseId), so those can't target the row frontend-only.
  onCompleteTask: (taskId: string) => void
  onDeleteTask: (taskId: string) => void
  onDeleteReminder: (reminderId: string) => void
  onDeleteAppointment: (appointmentId: string, applyTo: 'series' | 'occurrence') => void
  onCompleteDeadline: (deadlineId: string) => void
  onDeleteDeadline: (deadlineId: string) => void
}

const CalendarTimeGrid: React.FC<CalendarTimeGridProps> = ({
  days,
  loading = false,
  fade = 'none',
  outlookLate = false,
  statusSlot,
  entries,
  dayNotes,
  reminders,
  onPrev,
  onNext,
  onToday,
  onViewDay,
  onOpenTask,
  onOpenCase,
  onOpenAppointment,
  onOpenReminder,
  onNewTaskAt,
  onNewDeadlineAt,
  onNewAppointmentAt,
  onContextMenu,
  rangeLabel,
  onMoveEntry,
  onCompleteTask,
  onDeleteTask,
  onDeleteReminder,
  onDeleteAppointment,
  onCompleteDeadline,
  onDeleteDeadline,
}) => {
  const todayKey = localKey(new Date())

  // ── DRAG-TO-MOVE (pointer-based) ─────────────────────────
  // A grip on each timed appointment/reminder starts a pointer drag. We map
  // the pointer to a target time (Y → hour, snapped to 15 min) and, in Week
  // view, a target day (X → column), show a live dashed preview, and on drop
  // persist via onMoveEntry. Pointer-based (not HTML5 DnD) for precise
  // snapping + a controlled preview, and to cleanly distinguish click (open)
  // from drag (move) via a small movement threshold.
  const bodyGridRef = useRef<HTMLDivElement>(null)
  const GUTTER_PX = 48 // the 3rem time-label column
  const SNAP_MIN = 15
  const DRAG_THRESHOLD = 5
  // w/h = the dragged item's actual rendered size, captured at drag start, so
  // the preview is the SAME size as where it'll land.
  const dragStartRef = useRef<{ entry: CalendarEntry; x: number; y: number; moved: boolean; w: number; h: number } | null>(null)
  const [dragPreview, setDragPreview] = useState<
    { entry: CalendarEntry; left: number; top: number; width: number; height: number; label: string; iso: string } | null
  >(null)

  // Map a screen point to a snapped target { iso, preview coords, label }.
  const computeDropTarget = (
    clientX: number,
    clientY: number,
  ): { left: number; top: number; width: number; label: string; iso: string } | null => {
    const el = bodyGridRef.current
    if (!el || columns.length === 0) return null
    const rect = el.getBoundingClientRect()
    const nCols = columns.length
    const colW = (rect.width - GUTTER_PX) / nCols
    let dayIndex = Math.floor((clientX - rect.left - GUTTER_PX) / colW)
    dayIndex = Math.max(0, Math.min(nCols - 1, dayIndex))
    let hourFloat = START_HOUR + (clientY - rect.top - PAD_TOP) / HOUR_PX
    hourFloat = Math.max(START_HOUR, Math.min(END_HOUR, hourFloat))
    const totalMin = Math.round((hourFloat * 60) / SNAP_MIN) * SNAP_MIN
    const hh = Math.floor(totalMin / 60)
    const mm = totalMin % 60
    const day = columns[dayIndex].date
    // LOCAL wall-clock parts → ISO (matches how appointments/reminders store
    // their time), so the persisted instant reads back at the dropped time.
    const start = new Date(day.getFullYear(), day.getMonth(), day.getDate(), hh, mm)
    const snappedFloat = hh + mm / 60
    return {
      left: rect.left + GUTTER_PX + dayIndex * colW,
      top: rect.top + PAD_TOP + (snappedFloat - START_HOUR) * HOUR_PX,
      width: colW,
      label: `${day.toLocaleDateString('en-GB', { weekday: 'short' })} ${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`,
      iso: start.toISOString(),
    }
  }
  const beginDrag = (e: React.PointerEvent, entry: CalendarEntry): void => {
    if (e.button !== 0) return // left button only — right-click opens the menu
    // The accent-bar handle starts a drag; stop it reaching the block's click.
    e.stopPropagation()
    e.preventDefault()
    try { (e.currentTarget as Element).setPointerCapture(e.pointerId) } catch { /* ignore */ }
    // The handle's parent is the item wrapper → its rendered w/h (incl. any
    // lane split) is the item's real size, used for a matching preview.
    const wrap = (e.currentTarget as HTMLElement).parentElement
    dragStartRef.current = {
      entry,
      x: e.clientX,
      y: e.clientY,
      moved: false,
      w: wrap?.offsetWidth ?? 0,
      h: wrap?.offsetHeight ?? 0,
    }
  }
  const moveDrag = (e: React.PointerEvent): void => {
    const s = dragStartRef.current
    if (!s) return
    if (!s.moved && Math.hypot(e.clientX - s.x, e.clientY - s.y) < DRAG_THRESHOLD) return
    s.moved = true
    const t = computeDropTarget(e.clientX, e.clientY)
    // Preview = the dragged item's SIZE (w/h) at the snapped target slot.
    if (t) setDragPreview({ entry: s.entry, ...t, width: s.w || t.width, height: s.h || CHIP_PX })
  }
  const endDrag = (e: React.PointerEvent): void => {
    const s = dragStartRef.current
    dragStartRef.current = null
    setDragPreview(null)
    if (!s || !s.moved) return // a click, not a drag → the block's onClick opens it
    const t = computeDropTarget(e.clientX, e.clientY)
    if (t) onMoveEntry(s.entry, t.iso)
  }
  // Is this rendered entry the one currently being dragged? (dim it.)
  const isEntryDragging = (entry: CalendarEntry): boolean => {
    const d = dragPreview?.entry
    if (!d) return false
    if (entry.type === 'appointment') return !!entry.appointmentId && d.appointmentId === entry.appointmentId
    if (entry.type === 'reminder') return !!entry.reminderId && d.reminderId === entry.reminderId
    if (entry.type === 'task') return !!entry.taskId && d.taskId === entry.taskId
    return false
  }
  // Appointments, timed reminders + timed tasks can be moved to a new time;
  // a deadline (all-day, with a row id) can be moved to a new DAY. All use the
  // move interaction; only the first three are also accent-bar draggable.
  const isMovable = (entry: CalendarEntry): boolean =>
    entry.type === 'appointment' ||
    entry.type === 'reminder' ||
    entry.type === 'task' ||
    (entry.type === 'case_deadline' && !!entry.deadlineId)

  // ── RIGHT-CLICK ITEM MENU + MOVE MODE ────────────────────
  const [itemMenu, setItemMenu] = useState<{ x: number; y: number; entry: CalendarEntry } | null>(null)
  // Move mode: after "Move" in the menu, the next time-slot click places the
  // item there (for people who won't drag). Esc / a successful move clears it.
  const [moveMode, setMoveMode] = useState<CalendarEntry | null>(null)
  useEffect(() => {
    if (!moveMode) return
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') setMoveMode(null) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [moveMode])

  // Per-type menu items. Open/complete/delete reuse the parent handlers.
  const buildItemMenu = (entry: CalendarEntry): CalendarMenuItem[] => {
    const items: CalendarMenuItem[] = []
    if (isMovable(entry)) items.push({ label: 'Move', icon: <MenuIcon name="move" />, onClick: () => setMoveMode(entry) })
    if (entry.type === 'appointment') {
      // "Open" (not "Edit"): this opens the appointment VIEW, where the Edit
      // button is permission-gated — a view-only user mustn't be promised edit.
      items.push({ label: 'Open', icon: <MenuIcon name="external" />, onClick: () => entry.appointmentId && onOpenAppointment(entry.appointmentId) })
      if (entry.caseId) items.push({ label: 'Open case', icon: <MenuIcon name="briefcase" />, onClick: () => onOpenCase(entry.caseId as string) })
      if (entry.recurring) {
        items.push({ label: 'Delete this occurrence', icon: <MenuIcon name="trash" />, danger: true, separated: true, onClick: () => entry.appointmentId && onDeleteAppointment(entry.appointmentId, 'occurrence') })
        items.push({ label: 'Delete whole series', icon: <MenuIcon name="trash" />, danger: true, onClick: () => entry.appointmentId && onDeleteAppointment(entry.appointmentId, 'series') })
      } else {
        items.push({ label: 'Delete', icon: <MenuIcon name="trash" />, danger: true, separated: true, onClick: () => entry.appointmentId && onDeleteAppointment(entry.appointmentId, 'series') })
      }
    } else if (entry.type === 'task') {
      items.push({ label: 'Open', icon: <MenuIcon name="external" />, onClick: () => entry.taskId && onOpenTask(entry.taskId) })
      items.push({ label: 'Complete', icon: <MenuIcon name="check" />, onClick: () => entry.taskId && onCompleteTask(entry.taskId) })
      items.push({ label: 'Delete', icon: <MenuIcon name="trash" />, danger: true, separated: true, onClick: () => entry.taskId && onDeleteTask(entry.taskId) })
    } else if (entry.type === 'reminder') {
      items.push({ label: 'Edit', icon: <MenuIcon name="edit" />, onClick: () => onOpenReminder(new Date(entry.date)) })
      items.push({ label: 'Delete', icon: <MenuIcon name="trash" />, danger: true, separated: true, onClick: () => entry.reminderId && onDeleteReminder(entry.reminderId) })
    } else if (entry.type === 'case_deadline') {
      // Move (day change) is added at the top via isMovable. Case-scoped
      // actions here reuse the deadline update/delete endpoints.
      if (entry.caseId) items.push({ label: 'Open case', icon: <MenuIcon name="briefcase" />, onClick: () => onOpenCase(entry.caseId as string) })
      if (entry.deadlineId) items.push({ label: 'Complete', icon: <MenuIcon name="check" />, onClick: () => onCompleteDeadline(entry.deadlineId as string) })
      if (entry.deadlineId) items.push({ label: 'Delete', icon: <MenuIcon name="trash" />, danger: true, separated: true, onClick: () => onDeleteDeadline(entry.deadlineId as string) })
    } else if (entry.type === 'outlook') {
      // READ-ONLY: Outlook events live in Outlook, not Casevo. The only action
      // is to open the event there (no edit/complete/delete/move — isMovable is
      // false for outlook, so no Move item either).
      if (entry.webLink) items.push({ label: 'Open in Outlook', icon: <MenuIcon name="external" />, onClick: () => openOutlookLink(entry.webLink) })
    }
    return items
  }
  const openItemMenu = (e: React.MouseEvent, entry: CalendarEntry): void => {
    e.preventDefault()
    e.stopPropagation()
    setItemMenu({ x: e.clientX, y: e.clientY, entry })
  }
  // In move mode, a slot click places the item at that (snapped) instant.
  const placeMove = (e: React.MouseEvent): void => {
    const t = computeDropTarget(e.clientX, e.clientY)
    if (t && moveMode) onMoveEntry(moveMode, t.iso)
    setMoveMode(null)
  }

  // Click-to-add popover: opened by clicking an empty hour slot (hour set)
  // or an empty all-day cell (hour null). Positioned at the click point
  // (fixed), offering New task / New deadline. Closed on choose / outside.
  const [createAt, setCreateAt] = useState<{
    date: Date
    hour: number | null
    x: number
    y: number
  } | null>(null)
  const openCreate = (e: React.MouseEvent, date: Date, hour: number | null): void => {
    setCreateAt({ date, hour, x: e.clientX, y: e.clientY })
  }

  // Index entries/notes/reminders by civil day once.
  const { entriesByDay, notedDays, reminderDays } = useMemo(() => {
    const map = new Map<string, CalendarEntry[]>()
    for (const e of entries) {
      // Done tasks drop off the diary (they live on the Tasks list).
      if (e.type === 'task' && e.status === 'done') continue
      const key = entryDayKey(e)
      const bucket = map.get(key)
      if (bucket) bucket.push(e)
      else map.set(key, [e])
    }
    const noted = new Set<string>()
    for (const n of dayNotes) noted.add(utcKey(n.date))
    const reminded = new Set<string>()
    for (const r of reminders) reminded.add(reminderDayKey(r))
    return { entriesByDay: map, notedDays: noted, reminderDays: reminded }
  }, [entries, dayNotes, reminders])

  // Build per-column data: split all-day vs timed, place timed by local hour.
  const columns = useMemo<ColumnData[]>(() => {
    return days.map((date) => {
      const key = localKey(date)
      const dayEntries = entriesByDay.get(key) ?? []
      const allDay: CalendarEntry[] = []
      const timed: PlacedEntry[] = []
      for (const e of dayEntries) {
        if (!isTimedEntry(e)) {
          allDay.push(e)
          continue
        }
        const d = new Date(e.date)
        const hourFloat = d.getHours() + d.getMinutes() / 60
        // Clamp into the visible band so an out-of-hours item is still
        // reachable (pinned to the first/last row rather than lost).
        const clamped = Math.min(Math.max(hourFloat, START_HOUR), END_HOUR)
        // Appointments draw a sized BLOCK from start→end; tasks render as a
        // single-line chip (heightPx 0). A missing/invalid end → a default
        // ~1h block. Height clamped to a legible minimum.
        let heightPx = 0
        if (e.type === 'appointment' || e.type === 'outlook') {
          const end = e.endDate ? new Date(e.endDate) : null
          const endFloat = end && !isNaN(end.getTime())
            ? end.getHours() + end.getMinutes() / 60
            : hourFloat + 1
          const endClamped = Math.min(Math.max(endFloat, clamped + 0.25), END_HOUR + 1)
          heightPx = Math.max(20, (endClamped - clamped) * HOUR_PX)
        }
        timed.push({ entry: e, topPx: (clamped - START_HOUR) * HOUR_PX, heightPx, lane: 0, lanes: 1 })
      }
      // Lay overlapping items side-by-side (splits the column into lanes).
      if (outlookLate) {
        // Casevo items keep the layout they were revealed with; late Outlook
        // events are laned among themselves, and one that overlaps a Casevo
        // item becomes an inset overlay (see PlacedEntry.overlay).
        const own = timed.filter((t) => t.entry.type !== 'outlook')
        const ext = timed.filter((t) => t.entry.type === 'outlook')
        assignLanes(own)
        assignLanes(ext)
        const bottomOf = (t: PlacedEntry): number => t.topPx + (t.heightPx > 0 ? t.heightPx : CHIP_PX)
        for (const o of ext) {
          o.overlay = own.some((c) => c.topPx < bottomOf(o) && o.topPx < bottomOf(c))
        }
      } else {
        assignLanes(timed)
      }
      return {
        date,
        key,
        isToday: key === todayKey,
        allDay,
        timed,
        hasNote: notedDays.has(key),
        hasReminder: reminderDays.has(key),
      }
    })
  }, [days, entriesByDay, notedDays, reminderDays, todayKey, outlookLate])

  // "Now" line offset (local), only drawn in today's column when in-band.
  const now = new Date()
  const nowFloat = now.getHours() + now.getMinutes() / 60
  const nowInBand = nowFloat >= START_HOUR && nowFloat <= END_HOUR
  const nowTopPx = (nowFloat - START_HOUR) * HOUR_PX

  // Scroll the body so ~the current hour (or 8am) is in view on mount.
  const bodyRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!bodyRef.current) return
    const target = PAD_TOP + (nowInBand ? nowTopPx - HOUR_PX : (8 - START_HOUR) * HOUR_PX)
    bodyRef.current.scrollTop = Math.max(0, target)
    // Run once on mount / when the day-set changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days])

  const hours: number[] = []
  for (let h = START_HOUR; h <= END_HOUR; h++) hours.push(h)

  // ── SHARED COLUMN TEMPLATE (alignment fix) ───────────────
  // The header, all-day strip and hourly body must place their day columns
  // at the SAME pixel boundaries. Flex `flex-1` rounds each column
  // independently per container, so 7 columns over a width not divisible by
  // 7 accumulate a growing sub-pixel drift left→right (day 1 aligns, day 7
  // worst). A CSS grid with one shared template distributes the same
  // rounding identically across all three sibling grids, so every divider
  // lands on the same x. `3rem` = the w-12 time gutter; then one fr track
  // per day (minmax(0,1fr) so a track can shrink below content width).
  const colTemplate = `3rem repeat(${columns.length}, minmax(0, 1fr))`

  return (
    <div>
      {/* ── Range navigation header ─────────────────────────── */}
      {/* Wraps at phone width: the ‹ Today › cluster drops under the title
          rather than pushing the page wider. */}
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 mb-4" data-calendar-nav="">
        <h2 className="min-w-0 text-lg sm:text-xl font-medium text-casevo-forest">{rangeLabel}</h2>
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          {statusSlot}
          <button
            type="button"
            onClick={onToday}
            className="text-xs uppercase tracking-widest text-casevo-brand border border-casevo-border rounded-md px-3 py-1.5 hover:border-casevo-mid hover:bg-casevo-mint/30 transition-colors focus:outline-none focus-visible:border-casevo-mid"
          >
            Today
          </button>
          <button
            type="button"
            onClick={onPrev}
            aria-label="Previous"
            className="w-8 h-8 flex items-center justify-center text-casevo-mid border border-casevo-border rounded-md hover:border-casevo-mid hover:bg-casevo-mint/30 hover:text-casevo-forest transition-colors focus:outline-none focus-visible:border-casevo-mid"
          >
            ‹
          </button>
          <button
            type="button"
            onClick={onNext}
            aria-label="Next"
            className="w-8 h-8 flex items-center justify-center text-casevo-mid border border-casevo-border rounded-md hover:border-casevo-mid hover:bg-casevo-mint/30 hover:text-casevo-forest transition-colors focus:outline-none focus-visible:border-casevo-mid"
          >
            ›
          </button>
        </div>
      </div>

      {/* ── ONE shared scroll container (alignment fix) ─────────
          The header, all-day strip AND hourly body all live inside THIS
          single vertical scroll container, so its scrollbar reduces the
          inner width of ALL THREE grids EQUALLY. Each grid uses the same
          colTemplate over that same width, so the 7 day columns + their
          dividers are pixel-identical top-to-bottom at ANY window width or
          scrollbar width. (The previous approach hand-matched pr-2.5=10px
          on the header/strip against the body's scrollbar-gutter, but the
          REAL scrollbar width isn't always 10px — the mismatch split the
          fr tracks over different widths, drifting worse rightward.)
          The header + all-day strip are ONE sticky block so they stay
          pinned while the hours scroll under them. */}
      {loading && <span role="status" className="sr-only">Loading calendar…</span>}
      <div ref={bodyRef} className="overflow-y-auto max-h-[70vh]">
        <div className="sticky top-0 z-30">
          {/* ── Day-column headers ──────────────────────────── */}
          <div className="grid border-b border-casevo-border bg-casevo-offwhite" style={{ gridTemplateColumns: colTemplate }}>
            {/* Gutter spacer aligns headers with the hour-label column below. */}
            <div aria-hidden="true" />
            {columns.map((col) => (
              <button
                key={col.key}
                type="button"
                onClick={() => onViewDay(col.date)}
                onContextMenu={(e) => onContextMenu(e, col.date, null)}
                title="Open day view"
                className={`min-w-0 text-center py-2 border-l border-casevo-border transition-colors hover:bg-casevo-mint/30 focus:outline-none focus-visible:bg-casevo-mint/30 ${
                  col.isToday ? 'bg-casevo-mint/30' : ''
                }`}
              >
                <div className="text-[10px] uppercase tracking-widest text-casevo-mid">
                  {col.date.toLocaleDateString('en-GB', WEEKDAY_FMT)}
                </div>
                <div
                  className={`text-sm font-medium ${
                    col.isToday ? 'text-casevo-brand' : 'text-casevo-forest'
                  }`}
                >
                  {col.date.getDate()}
                </div>
              </button>
            ))}
          </div>

          {/* ── All-day strip ─────────────────────────────────── */}
          <div className="grid border-b border-casevo-border bg-casevo-offwhite" style={{ gridTemplateColumns: colTemplate }}>
            <div className="flex items-start justify-end pr-1.5 pt-1">
              <span className="text-[9px] uppercase tracking-widest text-casevo-mid">All-day</span>
            </div>
            {columns.map((col, colIdx) => (
              <div
                key={col.key}
                onClick={(e) => openCreate(e, col.date, null)}
                onContextMenu={(e) => onContextMenu(e, col.date, null)}
                className="min-w-0 border-l border-casevo-border p-1 flex flex-col gap-1 min-h-[36px] cursor-pointer hover:bg-casevo-mint/20 transition-colors"
              >
                <div className="flex items-center justify-end gap-0.5 leading-none h-3">
                  {!loading && col.hasReminder && (
                    <CalMarker label="Reminder" icon={<IcBell className="w-2.5 h-2.5" />} className="text-amber-600 dark:text-amber-400" />
                  )}
                  {!loading && col.hasNote && (
                    <CalMarker label="Day note" icon={<IcNote className="w-2.5 h-2.5" />} className="text-casevo-mid" />
                  )}
                </div>
                {loading && <SkeletonBar className={colIdx % 2 === 0 ? 'h-4 w-3/4' : 'h-4 w-1/2'} />}
                {!loading && col.allDay.map((entry, i) => (
                  // Right-click an all-day item → its per-type menu (stops the
                  // empty-cell add-menu). All-day items aren't drag-movable, but
                  // the menu still offers Open/Complete/Delete/Open case.
                  <div key={`${entry.type}-${entry.taskId ?? entry.reminderId ?? entry.caseId ?? ''}-${i}`} onContextMenu={(e) => openItemMenu(e, entry)} className={fadeClassFor(fade, entry.type === 'outlook').trim() || undefined}>
                    <TimeGridChip
                      entry={entry}
                      onOpenTask={onOpenTask}
                      onOpenCase={onOpenCase}
                      onOpenReminder={() => onOpenReminder(col.date)}
                    />
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* ── Hourly body ───────────────────────────────────── */}
        <div ref={bodyGridRef} className="grid" style={{ height: BODY_HEIGHT, gridTemplateColumns: colTemplate }}>
          {/* Hour-label gutter. */}
          <div className="relative">
            {hours.map((h) => (
              <div
                key={h}
                className="absolute right-1.5 -translate-y-1/2 text-[10px] text-casevo-mid"
                style={{ top: PAD_TOP + (h - START_HOUR) * HOUR_PX }}
              >
                {String(h).padStart(2, '0')}:00
              </div>
            ))}
          </div>
          {/* Day columns. */}
          {columns.map((col, colIdx) => (
            <div key={col.key} className="min-w-0 relative border-l border-casevo-border">
              {/* Click-to-add: one transparent button per hour, rendered
                  FIRST so it sits beneath the gridlines (pointer-events-none)
                  + the items (rendered after → on top). Clicking an empty
                  slot opens the create popover for that date + hour. */}
              {hours.map((h) => (
                <button
                  key={`slot-${h}`}
                  type="button"
                  onClick={(e) => { if (moveMode) { placeMove(e); return } openCreate(e, col.date, h) }}
                  onContextMenu={(e) => {
                    // Build the clicked date AT this hour so the menu's
                    // "Add task" is timed to the slot.
                    const at = new Date(col.date.getFullYear(), col.date.getMonth(), col.date.getDate(), h)
                    onContextMenu(e, at, h)
                  }}
                  aria-label={`Add at ${String(h).padStart(2, '0')}:00`}
                  className="absolute left-0 right-0 hover:bg-casevo-mint/20 focus:outline-none focus-visible:bg-casevo-mint/30 transition-colors"
                  style={{ top: PAD_TOP + (h - START_HOUR) * HOUR_PX, height: HOUR_PX }}
                />
              ))}
              {/* Hour gridlines. border-casevo-border resolves to a subtle
                  tint in BOTH themes (light #c8ddd0 / dark #3c4d43). The old
                  /60 opacity modifier produced no valid colour for the
                  var-based token, so the border fell back to currentColor —
                  which in dark mode is the light text → harsh white lines. */}
              {hours.map((h) => (
                <div
                  key={h}
                  className="absolute left-0 right-0 border-t border-casevo-border pointer-events-none"
                  style={{ top: PAD_TOP + (h - START_HOUR) * HOUR_PX }}
                />
              ))}
              {/* Now line — only in today's column. */}
              {col.isToday && nowInBand && (
                <div
                  className="absolute left-0 right-0 z-10 pointer-events-none"
                  style={{ top: PAD_TOP + nowTopPx }}
                >
                  <div className="h-px bg-casevo-brand" />
                  <div className="absolute -left-1 -top-1 w-2 h-2 rounded-full bg-casevo-brand" />
                </div>
              )}
              {/* Loading → skeleton blocks at varied (fixed, per-column) hours
                  so each column reads as "items coming", not a stripe. */}
              {loading && [0, 1].filter((k) => k === 0 || colIdx % 2 === 0).map((k) => (
                <SkeletonBar
                  key={k}
                  className="absolute left-[3px] right-[3px] z-10"
                  style={{
                    top: PAD_TOP + (((colIdx * 3 + 2 + k * 4) % 9) + 1) * HOUR_PX + 2,
                    height: k === 0 ? HOUR_PX - 6 : CHIP_PX + 2,
                  }}
                />
              ))}
              {/* Timed items — appointments as sized blocks, tasks as chips. */}
              {!loading && col.timed.map((placed, i) => {
                // Appointments + reminders are drag-to-move (they're timed).
                const draggable = isMovable(placed.entry)
                return (
                  <div
                    key={`${placed.entry.type}-${placed.entry.taskId ?? placed.entry.appointmentId ?? placed.entry.reminderId ?? placed.entry.caseId ?? ''}-${i}`}
                    className={`group absolute ${placed.overlay ? 'z-[25] shadow-md rounded-[3px]' : 'z-20'} ${isEntryDragging(placed.entry) ? 'opacity-40' : ''}${fadeClassFor(fade, placed.entry.type === 'outlook')}`}
                    style={placed.overlay ? {
                      // Late Outlook overlay: the right 40% of the column (laned
                      // among the late Outlook events), over the Casevo item it
                      // overlaps — which stays exactly where it was.
                      top: PAD_TOP + placed.topPx,
                      left: `calc(60% + ${(placed.lane / placed.lanes) * 40}% + 1px)`,
                      width: `calc(${(1 / placed.lanes) * 40}% - 2px)`,
                    } : {
                      top: PAD_TOP + placed.topPx,
                      // Lane split: left offset + width fraction of the column,
                      // with a ~1px gutter each side so adjacent lanes read apart.
                      left: `calc(${(placed.lane / placed.lanes) * 100}% + 1px)`,
                      width: `calc(${(1 / placed.lanes) * 100}% - 2px)`,
                    }}
                    onContextMenu={(e) => openItemMenu(e, placed.entry)}
                  >
                    {placed.entry.type === 'appointment' ? (
                      <AppointmentBlock
                        entry={placed.entry}
                        heightPx={placed.heightPx}
                        onOpen={onOpenAppointment}
                      />
                    ) : placed.entry.type === 'outlook' ? (
                      <OutlookBlock entry={placed.entry} heightPx={placed.heightPx} />
                    ) : (
                      <TimeGridChip
                        entry={placed.entry}
                        onOpenTask={onOpenTask}
                        onOpenCase={onOpenCase}
                        onOpenReminder={() => onOpenReminder(col.date)}
                        showTime
                      />
                    )}
                    {/* Drag handle = the LEFT ACCENT BAR: a slim strip over the
                        item's coloured left edge starts the pointer drag (grab
                        cursor). Only draggable items get it; the rest of the
                        item stays click-to-open. touch-none so a touch-drag
                        doesn't scroll the grid. */}
                    {draggable && (
                      <div
                        aria-label="Drag to reschedule"
                        title="Drag to reschedule"
                        onPointerDown={(e) => beginDrag(e, placed.entry)}
                        onPointerMove={moveDrag}
                        onPointerUp={endDrag}
                        onClick={(e) => e.stopPropagation()}
                        className="absolute left-0 top-0 h-full w-2 z-30 rounded-l-[3px]
                                   cursor-grab active:cursor-grabbing touch-none"
                      />
                    )}
                  </div>
                )
              })}
            </div>
          ))}
        </div>
      </div>

      {/* ── Drag preview ─────────────────────────────────────
          A live dashed target block (fixed to the viewport at the snapped
          slot) + the target day/time, shown while dragging an item. */}
      {dragPreview && (
        <div
          className="fixed z-[60] pointer-events-none rounded-[3px] border-2 border-dashed border-casevo-brand
                     bg-casevo-mint/50 dark:bg-casevo-light/20 flex items-start px-1.5 py-0.5
                     text-[10px] font-medium text-casevo-forest"
          style={{ left: dragPreview.left + 1, top: dragPreview.top, width: Math.max(0, dragPreview.width - 2), height: dragPreview.height }}
        >
          {dragPreview.label}
        </div>
      )}

      {/* ── Move mode banner ─────────────────────────────────
          After "Move" in an item's right-click menu: click a time slot to
          drop it there (or Esc / Cancel to abort). */}
      {moveMode && (
        <div className="fixed left-1/2 -translate-x-1/2 top-3 z-[210] flex items-center gap-3 rounded-full border border-casevo-border bg-casevo-surface shadow-lg px-4 py-1.5 text-xs text-casevo-forest">
          <span>Click a time to move <span className="font-medium">“{moveMode.title}”</span></span>
          <button
            type="button"
            onClick={() => setMoveMode(null)}
            className="text-[10px] uppercase tracking-widest text-casevo-brand hover:text-casevo-forest focus:outline-none casevo-focus"
          >
            Cancel
          </button>
        </div>
      )}

      {/* ── Right-click item menu (per type + Move) ──────────── */}
      {itemMenu && (
        <CalendarContextMenu
          x={itemMenu.x}
          y={itemMenu.y}
          items={buildItemMenu(itemMenu.entry)}
          onClose={() => setItemMenu(null)}
        />
      )}

      {/* ── Click-to-add popover ────────────────────────────── */}
      {createAt && (
        <>
          {/* Full-screen catcher closes the popover on any outside click. */}
          <div className="fixed inset-0 z-40" onClick={() => setCreateAt(null)} />
          <div
            className="fixed z-50 bg-casevo-surface border border-casevo-border rounded-lg shadow-lg p-1.5 w-72"
            style={{
              // Keep the popover on-screen near the click (simple clamp).
              // Right-edge margin matches the w-72 (288px) width + a little gap.
              left: Math.min(createAt.x, (typeof window !== 'undefined' ? window.innerWidth : 9999) - 300),
              top: Math.min(createAt.y, (typeof window !== 'undefined' ? window.innerHeight : 9999) - 110),
            }}
          >
            <div className="px-3 py-1.5 text-[10px] uppercase tracking-widest text-casevo-mid border-b border-casevo-border mb-1">
              {createAt.hour === null
                ? 'All-day'
                : `At ${String(createAt.hour).padStart(2, '0')}:00`}
            </div>
            <button
              type="button"
              onClick={() => { const c = createAt; setCreateAt(null); onNewTaskAt(c.date, c.hour) }}
              className="w-full text-left whitespace-nowrap px-3 py-1.5 text-sm text-casevo-forest rounded hover:bg-casevo-mint/40 transition-colors focus:outline-none casevo-focus"
            >
              ＋ New task
            </button>
            <button
              type="button"
              onClick={() => { const c = createAt; setCreateAt(null); onNewDeadlineAt(c.date, c.hour) }}
              className="w-full text-left whitespace-nowrap px-3 py-1.5 text-sm text-casevo-forest rounded hover:bg-casevo-mint/40 transition-colors focus:outline-none casevo-focus"
            >
              ＋ New deadline
            </button>
            <button
              type="button"
              onClick={() => { const c = createAt; setCreateAt(null); onNewAppointmentAt(c.date, c.hour) }}
              className="w-full text-left whitespace-nowrap px-3 py-1.5 text-sm text-casevo-forest rounded hover:bg-casevo-mint/40 transition-colors focus:outline-none casevo-focus"
            >
              ＋ New appointment
            </button>
          </div>
        </>
      )}
    </div>
  )
}

// ── CHIP ───────────────────────────────────────────────────
// Same colour language as the month grid: task = mint green, deadline =
// soft red with a red left border, reminder = a DISTINCT amber with a 🔔
// glyph (a personal item, apart from the firm's work items). Clickable —
// deadline/task route like the day panel; a reminder opens its day panel.
const TimeGridChip: React.FC<{
  entry: CalendarEntry
  onOpenTask: (taskId: string) => void
  onOpenCase: (caseId: string) => void
  onOpenReminder?: () => void
  showTime?: boolean
}> = ({ entry, onOpenTask, onOpenCase, onOpenReminder, showTime }) => {
  const isDeadline = entry.type === 'case_deadline'
  const isReminder = entry.type === 'reminder'
  const isOutlook = entry.type === 'outlook'
  // All item types share a border-l-4 left accent (consistent thickness =
  // consistent drag handle, matching the appointment block). Outlook =
  // a DISTINCT violet (apart from task-green / deadline-red / appointment-blue
  // / reminder-amber) so mirrored external events read as "from Outlook".
  const cls = isOutlook
    ? 'bg-violet-100 text-violet-900 border-l-4 border-violet-500 dark:bg-violet-900 dark:text-violet-100 dark:border-violet-400'
    : isReminder
      ? 'bg-amber-100 text-amber-950 border-l-4 border-amber-600 dark:bg-amber-900 dark:text-amber-100 dark:border-amber-400'
      : isDeadline
        ? 'bg-red-50 text-red-700 border-l-4 border-red-400 dark:bg-red-900 dark:text-red-200 dark:border-red-400/80'
        : 'bg-casevo-mint text-casevo-forest border-l-4 border-casevo-light'
  const handleClick = (e: React.MouseEvent): void => {
    // Stop the click reaching the slot / all-day cell behind (which would
    // open the create popover instead of the item).
    e.stopPropagation()
    if (entry.type === 'case_deadline' && entry.caseId) onOpenCase(entry.caseId)
    else if (entry.type === 'task' && entry.taskId) onOpenTask(entry.taskId)
    else if (entry.type === 'reminder') onOpenReminder?.()
    // Read-only Outlook item → open it in Outlook (the only action).
    else if (entry.type === 'outlook') openOutlookLink(entry.webLink)
  }
  const timeStr = showTime
    ? new Date(entry.date).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })
    : null
  // Diary batch: append the assignee ("· <name>"). Reminders lead with 🔔;
  // Outlook events lead with 📅 (+ a title tooltip marking their origin).
  // ★ Live assignee name by id (fallback: the server's assigneeName).
  const liveName = useLiveUserName()
  const assignee = entry.assigneeName ? liveName(entry.assigneeId, entry.assigneeName) : null
  const base = assignee ? `${entry.title} · ${assignee}` : entry.title
  // Feather icon (not emoji) leads reminder / Outlook chips; the tooltip
  // spells the kind out for pointer users, the icon is aria-labelled.
  const label = isReminder ? `Reminder: ${base}` : isOutlook ? `Outlook: ${base}` : base
  return (
    <button
      type="button"
      onClick={handleClick}
      title={
        isOutlook
          ? `${entry.title}${entry.location ? ` @ ${entry.location}` : ''} · From Outlook`
          : entry.caseLabel ? `${label} — ${entry.caseLabel}` : label
      }
      // Fade-in is applied by the grid's item wrapper (fadeClassFor).
      className={`block w-full text-left truncate rounded-[3px] px-1.5 py-0.5 text-[10px] sm:text-[11px] leading-tight transition-opacity hover:opacity-80 focus:outline-none casevo-focus ${cls}`}
    >
      {timeStr && <span className="font-medium mr-1">{timeStr}</span>}
      {isReminder && <CalMarker label="Reminder" icon={<IcBell className="w-2.5 h-2.5" />} className="mr-1 align-[-1px]" />}
      {isOutlook && <CalMarker label="From Outlook" icon={<IcCalendar className="w-2.5 h-2.5" />} className="mr-1 align-[-1px]" />}
      {base}
    </button>
  )
}

// ── APPOINTMENT BLOCK ──────────────────────────────────────
// A sized timed block for appointments — a DISTINCT blue family (not the
// task-green / deadline-red). COURT is rendered stronger (filled indigo)
// so hearings stand out from ordinary meetings/calls. Shows the start(–end)
// time; clicking opens the appointment for edit.
const apptTime = (iso: string): string =>
  new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })

const AppointmentBlock: React.FC<{
  entry: CalendarEntry
  heightPx: number
  onOpen: (appointmentId: string) => void
}> = ({ entry, heightPx, onOpen }) => {
  const court = entry.apptType === 'court'
  // Soft-tint, consistent with the task/deadline/reminder chips (tinted bg +
  // dark coloured text + a coloured left accent), never solid. COURT stays
  // DISTINCT via a different hue (indigo vs sky) + bold + the "court" tag.
  // The time-grid block carries a slightly heavier left accent bar
  // (border-l-4) so a sized event reads as more present than a chip.
  const cls = court
    ? 'bg-indigo-100 text-indigo-900 border-l-4 border-indigo-600 dark:bg-indigo-900 dark:text-indigo-100 dark:border-indigo-400 font-medium'
    : 'bg-sky-100 text-sky-900 border-l-4 border-sky-500 dark:bg-sky-900 dark:text-sky-100 dark:border-sky-400'
  const start = apptTime(entry.date)
  const end = entry.endDate ? apptTime(entry.endDate) : null
  // ★ Live assignee name by id (fallback: the server's assigneeName).
  const liveName = useLiveUserName()
  const assignee = entry.assigneeName ? liveName(entry.assigneeId, entry.assigneeName) : null
  const label = assignee ? `${entry.title} · ${assignee}` : entry.title
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); if (entry.appointmentId) onOpen(entry.appointmentId) }}
      title={`${start}${end ? `–${end}` : ''} · ${entry.title}${entry.location ? ` @ ${entry.location}` : ''}${entry.caseLabel ? ` — ${entry.caseLabel}` : ''}`}
      style={{ minHeight: heightPx }}
      className={`block w-full text-left overflow-hidden rounded-[3px] px-1.5 py-0.5 text-[10px] sm:text-[11px] leading-tight focus:outline-none casevo-focus hover:opacity-90 transition-opacity ${cls}`}
    >
      <span className="font-medium mr-1">{start}{end ? `–${end}` : ''}</span>
      {entry.recurring && <span className="mr-1" title="Repeats" aria-label="Repeats">↻</span>}
      {label}
      {court && <span className="ml-1 align-middle uppercase tracking-wide text-[8px] opacity-90">court</span>}
    </button>
  )
}

// ── OUTLOOK BLOCK ──────────────────────────────────────────
// A sized timed block for a READ-ONLY Outlook event — a DISTINCT violet family
// (apart from task-green / deadline-red / appointment-blue / reminder-amber),
// with a leading 📅 badge so it reads as "from Outlook". Clicking opens the
// event in Outlook (the only action — Casevo can't edit/move/delete it). No
// drag handle (isMovable is false for outlook).
const OutlookBlock: React.FC<{
  entry: CalendarEntry
  heightPx: number
}> = ({ entry, heightPx }) => {
  const start = apptTime(entry.date)
  const end = entry.endDate ? apptTime(entry.endDate) : null
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); openOutlookLink(entry.webLink) }}
      title={`${start}${end ? `–${end}` : ''} · ${entry.title}${entry.location ? ` @ ${entry.location}` : ''} · From Outlook`}
      style={{ minHeight: heightPx }}
      className="block w-full text-left overflow-hidden rounded-[3px] px-1.5 py-0.5 text-[10px] sm:text-[11px] leading-tight focus:outline-none casevo-focus hover:opacity-90 transition-opacity bg-violet-100 text-violet-900 border-l-4 border-violet-500 dark:bg-violet-900 dark:text-violet-100 dark:border-violet-400"
    >
      <span className="font-medium mr-1">{start}{end ? `–${end}` : ''}</span>
      <CalMarker label="From Outlook" icon={<IcCalendar className="w-2.5 h-2.5" />} className="mr-1 align-[-1px]" />
      {entry.title}
    </button>
  )
}

export default CalendarTimeGrid
